import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import contextToiTeaser from '@contexts/toiTeaser'
import { TOITeaserLayoutContextType } from '@contexts/toiTeaser'
import { SubtitleProps } from '@widgets/Teaser/toi/types'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import { ViewportTypeValues } from '@contexts/viewport'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

interface StyledSubtitleProps
  extends Pick<TOITeaserLayoutContextType, 'teaserFlex' | 'articleType'> {
  alignment: TOITeaserLayoutContextType['mainTextAlignment']['h']
}

const { TOITeaserLayoutContext } = contextToiTeaser

const StyledSubtitle = styled.div<StyledSubtitleProps>`
  ${({
    theme: {
      fonts: { goodOT: goodOTFontName },
      colors: {
        white,
        grey: { grey_80, grey_100 },
      },
    },
    teaserFlex,
    alignment,
    articleType,
  }) => {
    let fontSizeDesktop
    let cornerPaddingDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.02976 * teaserWidthDesktop}px`
      cornerPaddingDesktop = '16px'
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.04518 * teaserWidthDesktop}px`
      cornerPaddingDesktop = '16px'
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.04878 * teaserWidthDesktop}px`
      cornerPaddingDesktop = '12px'
    } else if (desktopTeaserFlex === '2') {
      fontSizeDesktop = `${0.05312 * teaserWidthDesktop}px`
      cornerPaddingDesktop = '8px'
    }

    let fontSizeTablet
    let cornerPaddingTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.04901 * teaserWidthTablet}px`
      cornerPaddingTablet = '16px'
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.05743 * teaserWidthTablet}px`
      cornerPaddingTablet = '12px'
    }

    const cornerPaddingMobile = '3vw'

    return css`
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      font-weight: 500;
      line-height: 1.1;
      ${articleType === 'default' &&
      css`
        text-shadow: 0 0 8px ${grey_100}, 0 0 8px ${grey_100};
      `}
      color: ${articleType === 'column' ? grey_80 : white};
      margin-top: 5px;
      > strong {
        color: ${articleType === 'column' ? grey_80 : white};
      }
      > p {
        color: ${articleType === 'column' ? grey_80 : white};
        margin: 0;
      }
      ${articleType !== 'default' &&
      css`
        position: absolute;
        bottom: 0;
        ${alignment}: 0;
        box-sizing: border-box;
      `};
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
        ${articleType !== 'default' &&
        css`
          ${desktopTeaserFlex === '6'
            ? css`
                width: 100%;
              `
            : css`
                width: 50%;
              `};
          margin: 0 calc(${cornerPaddingDesktop} + 8px);
        `};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
        ${articleType !== 'default' &&
        css`
          width: 50%;
          margin: 0 calc(${cornerPaddingTablet} + 8px);
        `};
      `)}

      ${mobileCSS(css`
        font-size: 5.3vw;
        ${articleType !== 'default' &&
        css`
          width: 50%;
          margin: 0 calc(${cornerPaddingMobile} + 8px);
        `};
      `)}
    `
  }}
`

const Subtitle: FunctionComponent<SubtitleProps> = ({ children }) => {
  const { teaserFlex, articleType, mainTextAlignment } = useContext(
    TOITeaserLayoutContext
  )

  return (
    <StyledSubtitle
      teaserFlex={teaserFlex}
      articleType={articleType}
      alignment={mainTextAlignment['h']}
      dangerouslySetInnerHTML={{
        __html: children,
      }}
    />
  )
}

export default Subtitle
